import { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import {
  Settings,
  BarChart3,
  List,
  RefreshCcw,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Loader2,
  Ban,
  User,
  Inbox,
  Eye,
  Instagram,
  FileText,
  X,
  Filter,
  Menu,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useSidebar } from 'contexts/SidebarContext';
import {
  getJobStats,
  getSuperAdminJobs,
  clearAllJobs,
  clearJobsByStatus,
  clearJobsByUser,
  getSuperAdminUsers,
  getSchedulerStatus,
  enableScheduler,
  disableScheduler,
  getExecutionLogs,
  JobStats,
  SuperAdminJob,
  SuperAdminUser,
  SuperAdminJobsQueryParams,
  SchedulerStatusResponse,
  ExecutionLog
} from 'services/admin/superAdminService';
import Spinner from 'components/common/Spinner';
import { logger } from 'utils/logger';
import { getErrorMessage } from 'utils/errors';

export default function JobManagement() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Theme color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const cardBorder = isDark ? '#1E293B' : '#E2E8F0';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableBorderColor = isDark ? '#1E293B' : '#E2E8F0';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#1E293B';
  const buttonBg = isDark ? '#1D293D' : '#F3F4F6';
  const buttonText = isDark ? '#90A1B9' : '#374151';
  const buttonHoverBg = isDark ? '#253449' : '#E5E7EB';
  const activeTabBg = isDark ? '#1C398E' : '#EFF6FF';
  const activeTabText = isDark ? '#51A2FF' : '#2B7FFF';
  const paginationBg = isDark ? '#1D293D' : '#FFFFFF';
  const paginationBorder = isDark ? '#314158' : '#D1D5DB';
  const paginationText = isDark ? '#90A1B9' : '#374151';
  const successBg = isDark ? '#0D2818' : '#F0FDF4';
  const successBorder = isDark ? '#1A4028' : '#BBF7D0';
  const successText = isDark ? '#5EE9B5' : '#166534';
  const errorBg = isDark ? '#2A1215' : '#FEF2F2';
  const errorBorder = isDark ? '#4A1C20' : '#FECACA';
  const errorText = isDark ? '#F87171' : '#991B1B';
  const modalBackdrop = isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)';
  const modalBg = isDark ? '#1D293D' : '#FFFFFF';
  const modalInfoBg = isDark ? '#111827' : '#F9FAFB';

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Job stats state
  const [jobStats, setJobStats] = useState<JobStats | null>(null);

  // Jobs listing state
  const [jobs, setJobs] = useState<SuperAdminJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsPagination, setJobsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Jobs filters state
  const [jobsFilters, setJobsFilters] = useState<SuperAdminJobsQueryParams>({
    page: 1,
    limit: 20,
    status: '',
    startDate: '',
    endDate: '',
    sortBy: 'created_on',
    sortOrder: 'desc'
  });

  // Tab state (stats vs jobs list)
  const [activeTab, setActiveTab] = useState<'stats' | 'jobs'>('stats');
  const [showFilters, setShowFilters] = useState(false);

  // Users for filtering
  const [users, setUsers] = useState<SuperAdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Action states
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [showClearByStatusModal, setShowClearByStatusModal] = useState(false);
  const [showClearByUserModal, setShowClearByUserModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [beforeDate, setBeforeDate] = useState<string>('');

  // Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatusResponse | null>(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);

  // Job details modal state
  const [selectedJob, setSelectedJob] = useState<SuperAdminJob | null>(null);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);

  // Execution logs state
  const [executionLogs, setExecutionLogs] = useState<ExecutionLog[]>([]);
  const [executionLogsLoading, setExecutionLogsLoading] = useState(false);
  const [logsAutoRefresh, setLogsAutoRefresh] = useState(true);
  const [executionLogsPagination, setExecutionLogsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Fetch job stats
  const fetchJobStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getJobStats();
      setJobStats(response.stats || null);
    } catch (err: unknown) {
      logger.error('Failed to fetch job stats:', err);
      setError(getErrorMessage(err, 'Failed to fetch job statistics'));
      // Set default empty stats so UI still renders
      setJobStats({
        total: 0,
        byStatus: {
          pending: 0,
          processing: 0,
          completed: 0,
          failed: 0,
          cancelled: 0
        }
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch jobs list
  const fetchJobs = useCallback(async () => {
    try {
      setJobsLoading(true);
      const params: SuperAdminJobsQueryParams = {
        page: jobsFilters.page,
        limit: jobsFilters.limit,
        sortBy: jobsFilters.sortBy,
        sortOrder: jobsFilters.sortOrder
      };

      if (jobsFilters.status) params.status = jobsFilters.status;
      if (jobsFilters.startDate) params.startDate = jobsFilters.startDate;
      if (jobsFilters.endDate) params.endDate = jobsFilters.endDate;
      if (jobsFilters.userId) params.userId = jobsFilters.userId;
      if (jobsFilters.accountId) params.accountId = jobsFilters.accountId;

      const response = await getSuperAdminJobs(params);
      setJobs(response.jobs || []);
      setJobsPagination(response.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
      });
    } catch (err: unknown) {
      logger.error('Failed to fetch jobs:', err);
      setError(getErrorMessage(err, 'Failed to fetch jobs'));
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [jobsFilters]);

  // Fetch users for user selection
  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const response = await getSuperAdminUsers({
        page: 1,
        limit: 50,
        search: userSearchTerm || undefined
      });
      setUsers(response.users || []);
    } catch (err: unknown) {
      logger.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [userSearchTerm]);

  // Fetch scheduler status
  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const response = await getSchedulerStatus();
      setSchedulerStatus(response);
    } catch (err: unknown) {
      logger.error('Failed to fetch scheduler status:', err);
      // Set default status so UI still renders
      setSchedulerStatus(null);
    }
  }, []);

  // Toggle scheduler (enable/disable)
  const handleToggleScheduler = async () => {
    try {
      setSchedulerLoading(true);
      if (schedulerStatus?.enabled) {
        const response = await disableScheduler();
        setSchedulerStatus(response);
        setSuccessMessage(t('superAdmin.jobManagement.scheduler.disabled', 'Scheduler disabled successfully'));
      } else {
        const response = await enableScheduler();
        setSchedulerStatus(response);
        setSuccessMessage(t('superAdmin.jobManagement.scheduler.enabled', 'Scheduler enabled successfully'));
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setSchedulerLoading(false);
    }
  };

  useEffect(() => {
    fetchJobStats();
    fetchSchedulerStatus();
  }, [fetchJobStats, fetchSchedulerStatus]);

  // Fetch jobs when tab changes to jobs or filters change
  useEffect(() => {
    if (activeTab === 'jobs') {
      fetchJobs();
    }
  }, [activeTab, fetchJobs]);

  useEffect(() => {
    if (showClearByUserModal) {
      fetchUsers();
    }
  }, [showClearByUserModal, fetchUsers]);

  // Handle clear all jobs
  const handleClearAllJobs = async () => {
    try {
      setActionLoading(true);
      const response = await clearAllJobs(beforeDate || undefined);
      setSuccessMessage(`${response.deletedCount} jobs cleared successfully`);
      setShowClearAllModal(false);
      setBeforeDate('');
      fetchJobStats();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle clear jobs by status
  const handleClearByStatus = async () => {
    if (!selectedStatus) return;

    try {
      setActionLoading(true);
      const response = await clearJobsByStatus(selectedStatus);
      setSuccessMessage(`${response.deletedCount} ${selectedStatus} jobs cleared successfully`);
      setShowClearByStatusModal(false);
      setSelectedStatus('');
      fetchJobStats();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle clear jobs by user
  const handleClearByUser = async () => {
    if (!selectedUserId) return;

    try {
      setActionLoading(true);
      const response = await clearJobsByUser(selectedUserId);
      setSuccessMessage(`${response.deletedCount} jobs cleared for user`);
      setShowClearByUserModal(false);
      setSelectedUserId('');
      fetchJobStats();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Quick clear by status
  const handleQuickClearByStatus = async (status: string) => {
    try {
      setActionLoading(true);
      const response = await clearJobsByStatus(status);
      setSuccessMessage(`${response.deletedCount} ${status} jobs cleared successfully`);
      fetchJobStats();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Format number
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Calculate percentage
  const calculatePercentage = (value: number, total: number) => {
    if (total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  // Format date
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      pending: { bg: isDark ? '#2A2008' : '#FEF9C3', text: isDark ? '#FACC15' : '#A16207', icon: <Clock size={12} /> },
      processing: { bg: isDark ? '#0C1A2E' : '#DBEAFE', text: isDark ? '#60A5FA' : '#1D4ED8', icon: <RefreshCcw size={12} /> },
      completed: { bg: isDark ? '#0D2818' : '#DCFCE7', text: isDark ? '#5EE9B5' : '#15803D', icon: <CheckCircle2 size={12} /> },
      failed: { bg: isDark ? '#2A1215' : '#FEE2E2', text: isDark ? '#F87171' : '#DC2626', icon: <XCircle size={12} /> },
      cancelled: { bg: isDark ? '#1D293D' : '#F1F5F9', text: isDark ? '#90A1B9' : '#475569', icon: <Ban size={12} /> }
    };
    const config = statusConfig[status.toLowerCase()] || statusConfig.pending;
    return (
      <span style={{ background: config.bg, color: config.text, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '9999px', fontSize: '12px', fontWeight: 500 }}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Format job type (can be string or array)
  const formatJobType = (jobType: string | string[] | undefined) => {
    if (!jobType) return '-';
    if (Array.isArray(jobType)) {
      return jobType.map(type => type.replace(/_/g, ' ')).join(', ');
    }
    return jobType.replace(/_/g, ' ');
  };

  // Open job details modal
  const openJobDetails = (job: SuperAdminJob) => {
    setSelectedJob(job);
    setShowJobDetailsModal(true);
  };

  // Track current logs page for silent refresh
  const logsPageRef = useRef(1);

  // Fetch execution logs for a job (showLoading=false for silent refresh)
  const fetchExecutionLogs = useCallback(async (jobId: string, page: number = 1, showLoading: boolean = true) => {
    try {
      if (showLoading) setExecutionLogsLoading(true);
      const response = await getExecutionLogs({ job_id: jobId, page, limit: 20 });
      setExecutionLogs(response.logs || []);
      setExecutionLogsPagination({
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.total_pages || 0
      });
      logsPageRef.current = page;
    } catch (err: unknown) {
      logger.error('Failed to fetch execution logs:', err);
      if (showLoading) setExecutionLogs([]);
    } finally {
      if (showLoading) setExecutionLogsLoading(false);
    }
  }, []);

  // Handle execution logs page change
  const handleLogsPageChange = (newPage: number) => {
    if (selectedJob) {
      fetchExecutionLogs(selectedJob._id, newPage);
    }
  };

  // Get log level badge
  const getLogLevelBadge = (level: string) => {
    const styles: Record<string, { bg: string; color: string }> = {
      info: { bg: isDark ? '#0C1A2E' : '#DBEAFE', color: isDark ? '#60A5FA' : '#1D4ED8' },
      warning: { bg: isDark ? '#2A1F0A' : '#FEF3C7', color: isDark ? '#FBBF24' : '#92400E' },
      error: { bg: isDark ? '#2A1215' : '#FEE2E2', color: isDark ? '#F87171' : '#DC2626' },
      debug: { bg: isDark ? '#1D293D' : '#F3F4F6', color: isDark ? '#90A1B9' : '#6B7280' }
    };
    const s = styles[level] || styles.debug;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', padding: '1px 8px',
        borderRadius: '4px', fontSize: '11px', fontWeight: 600,
        background: s.bg, color: s.color, textTransform: 'uppercase'
      }}>
        {level}
      </span>
    );
  };

  // Initial fetch when modal opens
  useEffect(() => {
    if (showJobDetailsModal && selectedJob) {
      logsPageRef.current = 1;
      fetchExecutionLogs(selectedJob._id, 1);
    } else {
      setExecutionLogs([]);
      setExecutionLogsPagination({ page: 1, limit: 20, total: 0, totalPages: 0 });
      logsPageRef.current = 1;
      setLogsAutoRefresh(true);
    }
  }, [showJobDetailsModal, selectedJob, fetchExecutionLogs]);

  // Silent polling every 5s (controlled by logsAutoRefresh toggle)
  useEffect(() => {
    if (!showJobDetailsModal || !selectedJob || !logsAutoRefresh) return;

    const intervalId = setInterval(() => {
      fetchExecutionLogs(selectedJob._id, logsPageRef.current, false);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [showJobDetailsModal, selectedJob, logsAutoRefresh, fetchExecutionLogs]);

  // Handle jobs filter change
  const handleFilterChange = (key: keyof SuperAdminJobsQueryParams, value: string) => {
    setJobsFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : Number(value) // Reset to page 1 when other filters change
    }));
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setJobsFilters(prev => ({ ...prev, page: newPage }));
  };

  // Reset filters
  const resetFilters = () => {
    setJobsFilters({
      page: 1,
      limit: 20,
      status: '',
      startDate: '',
      endDate: '',
      sortBy: 'created_on',
      sortOrder: 'desc'
    });
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return <Spinner size="large" text={t('superAdmin.loading', 'Loading job stats...')} fullPage />;
  }

  return (
    <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ gap: '8px', background: containerBg }}>
      {/* Main Card */}
      <div className="flex flex-col items-start flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl" style={{ background: mainBg, boxShadow: mainShadow }}>

        {/* Header */}
        <div className="flex flex-col justify-center items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl" style={{ padding: '12px 16px', gap: '8px', borderBottom: `1px solid ${headerBorder}` }}>
          {/* Row 1: hamburger + icon + title */}
          <div className="flex flex-row items-center w-full" style={{ gap: '8px' }}>
            {/* Hamburger menu for mobile */}
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            {/* Icon */}
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', background: isDark ? '#1C398E' : '#EFF6FF', borderRadius: '12px', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Settings size={20} style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }} />
            </div>
            {/* Title */}
            <h1 style={{ fontFamily: 'Geist, sans-serif', fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0, whiteSpace: 'nowrap' }}>
              {t('superAdmin.jobManagement.title', 'Job Management')}
            </h1>
          </div>
          {/* Row 2: tabs */}
          <div className="w-full overflow-x-auto">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}>
              {/* Segmented Tab Control */}
              <div style={{ display: 'flex', border: `1px solid ${headerBorder}`, borderRadius: '8px', overflow: 'hidden', height: '36px' }}>
                <button
                  onClick={() => setActiveTab('stats')}
                  style={{
                    padding: '0 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'stats' ? activeTabBg : 'transparent',
                    color: activeTab === 'stats' ? activeTabText : subtitleColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <BarChart3 size={14} />
                  Statistics
                </button>
                <button
                  onClick={() => setActiveTab('jobs')}
                  style={{
                    padding: '0 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'jobs' ? activeTabBg : 'transparent',
                    color: activeTab === 'jobs' ? activeTabText : subtitleColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <List size={14} />
                  Jobs List
                </button>
              </div>
            </div>
          </div>
          {/* Row 3: scheduler + actions - wraps on mobile */}
          <div className="flex flex-wrap items-center w-full" style={{ gap: '8px' }}>
            {/* Scheduler Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: schedulerStatus?.enabled ? (isDark ? '#5EE9B5' : '#16A34A') : (isDark ? '#F87171' : '#DC2626') }}>
                {schedulerStatus?.enabled
                  ? t('superAdmin.jobManagement.scheduler.running', 'Scheduler Running')
                  : t('superAdmin.jobManagement.scheduler.stopped', 'Scheduler Stopped')}
              </span>
              <button
                onClick={handleToggleScheduler}
                disabled={schedulerLoading}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  schedulerStatus?.enabled ? 'bg-green-500' : 'bg-gray-300'
                } ${schedulerLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="switch"
                aria-checked={schedulerStatus?.enabled}
                title={schedulerStatus?.enabled
                  ? t('superAdmin.jobManagement.scheduler.clickToDisable', 'Click to disable scheduler')
                  : t('superAdmin.jobManagement.scheduler.clickToEnable', 'Click to enable scheduler')}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    schedulerStatus?.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                >
                  {schedulerLoading && (
                    <Loader2 size={12} className="animate-spin absolute top-1 left-1" style={{ color: isDark ? '#90A1B9' : '#9CA3AF' }} />
                  )}
                </span>
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => {
                if (activeTab === 'stats') {
                  fetchJobStats();
                  fetchSchedulerStatus();
                } else {
                  fetchJobs();
                }
              }}
              className="flex items-center justify-center transition-colors"
              style={{ width: '36px', height: '36px', borderRadius: '8px', background: buttonBg, color: buttonText, border: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = buttonHoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = buttonBg)}
              title={t('superAdmin.jobManagement.refresh', 'Refresh')}
            >
              <RefreshCcw size={16} />
            </button>

            {/* Filter Toggle Button */}
            {activeTab === 'jobs' && (
              <button
                onClick={() => setShowFilters(prev => !prev)}
                className="flex items-center gap-1.5 transition-colors"
                style={{
                  height: '36px',
                  padding: '0 12px',
                  borderRadius: '8px',
                  background: showFilters ? activeTabBg : buttonBg,
                  color: showFilters ? activeTabText : buttonText,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500
                }}
              >
                <Filter size={14} />
                Filters
                {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}

            {/* Clear All Jobs Button */}
            <button
              onClick={() => setShowClearAllModal(true)}
              className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
              style={{ height: '36px' }}
            >
              <Trash2 size={14} />
              {t('superAdmin.jobManagement.clearAllJobs', 'Clear All Jobs')}
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', flex: 1, minHeight: 0 }}>

          {/* Messages */}
          {successMessage && (
            <div className="p-3 rounded-lg text-sm flex items-center gap-2" style={{ background: successBg, border: `1px solid ${successBorder}`, color: successText }}>
              <CheckCircle2 size={16} />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="p-3 rounded-lg text-sm flex items-center gap-2" style={{ background: errorBg, border: `1px solid ${errorBorder}`, color: errorText }}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {/* Stats Overview */}
          {activeTab === 'stats' && jobStats && (
            <>
              {/* Total Jobs Card */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold" style={{ color: titleColor }}>Total Jobs Overview</h2>
                  <span className="text-2xl font-bold" style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }}>{formatNumber(jobStats.total)}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {/* Pending */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#2A2008' : '#FFFBEB', border: `1px solid ${isDark ? '#3D3010' : '#FEF3C7'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#FACC15' : '#A16207' }}>Pending</span>
                      <Clock size={16} style={{ color: isDark ? '#FACC15' : '#CA8A04' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#FACC15' : '#A16207' }}>{formatNumber(jobStats.byStatus.pending)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#FACC15' : '#CA8A04' }}>{calculatePercentage(jobStats.byStatus.pending, jobStats.total)}%</div>
                  </div>

                  {/* Processing */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#0C1A2E' : '#EFF6FF', border: `1px solid ${isDark ? '#162A4A' : '#DBEAFE'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#60A5FA' : '#1D4ED8' }}>Processing</span>
                      <RefreshCcw size={16} style={{ color: isDark ? '#60A5FA' : '#2563EB' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#60A5FA' : '#1D4ED8' }}>{formatNumber(jobStats.byStatus.processing)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#60A5FA' : '#2563EB' }}>{calculatePercentage(jobStats.byStatus.processing, jobStats.total)}%</div>
                  </div>

                  {/* Completed */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#0D2818' : '#F0FDF4', border: `1px solid ${isDark ? '#1A4028' : '#DCFCE7'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#5EE9B5' : '#15803D' }}>Completed</span>
                      <CheckCircle2 size={16} style={{ color: isDark ? '#5EE9B5' : '#16A34A' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#5EE9B5' : '#15803D' }}>{formatNumber(jobStats.byStatus.completed)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#5EE9B5' : '#16A34A' }}>{calculatePercentage(jobStats.byStatus.completed, jobStats.total)}%</div>
                  </div>

                  {/* Failed */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#2A1215' : '#FEF2F2', border: `1px solid ${isDark ? '#4A1C20' : '#FEE2E2'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#F87171' : '#DC2626' }}>Failed</span>
                      <XCircle size={16} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#F87171' : '#DC2626' }}>{formatNumber(jobStats.byStatus.failed)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#F87171' : '#EF4444' }}>{calculatePercentage(jobStats.byStatus.failed, jobStats.total)}%</div>
                  </div>

                  {/* Cancelled */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#1D293D' : '#F9FAFB', border: `1px solid ${isDark ? '#314158' : '#E5E7EB'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#90A1B9' : '#374151' }}>Cancelled</span>
                      <Ban size={16} style={{ color: isDark ? '#90A1B9' : '#6B7280' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#90A1B9' : '#374151' }}>{formatNumber(jobStats.byStatus.cancelled)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#90A1B9' : '#6B7280' }}>{calculatePercentage(jobStats.byStatus.cancelled, jobStats.total)}%</div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <h2 className="text-sm font-semibold mb-4" style={{ color: titleColor }}>Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Clear Completed Jobs */}
                  <button
                    onClick={() => handleQuickClearByStatus('completed')}
                    disabled={actionLoading || jobStats.byStatus.completed === 0}
                    className="p-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    style={{ border: `1px solid ${isDark ? '#1A4028' : '#BBF7D0'}` }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#0D2818' : '#DCFCE7' }}>
                        <CheckCircle2 size={20} style={{ color: isDark ? '#5EE9B5' : '#16A34A' }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium" style={{ color: titleColor }}>Clear Completed</h3>
                        <p className="text-xs" style={{ color: subtitleColor }}>{formatNumber(jobStats.byStatus.completed)} jobs</p>
                      </div>
                    </div>
                  </button>

                  {/* Clear Failed Jobs */}
                  <button
                    onClick={() => handleQuickClearByStatus('failed')}
                    disabled={actionLoading || jobStats.byStatus.failed === 0}
                    className="p-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    style={{ border: `1px solid ${isDark ? '#4A1C20' : '#FECACA'}` }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}>
                        <XCircle size={20} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium" style={{ color: titleColor }}>Clear Failed</h3>
                        <p className="text-xs" style={{ color: subtitleColor }}>{formatNumber(jobStats.byStatus.failed)} jobs</p>
                      </div>
                    </div>
                  </button>

                  {/* Clear Cancelled Jobs */}
                  <button
                    onClick={() => handleQuickClearByStatus('cancelled')}
                    disabled={actionLoading || jobStats.byStatus.cancelled === 0}
                    className="p-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    style={{ border: `1px solid ${isDark ? '#314158' : '#E5E7EB'}` }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#1D293D' : '#F3F4F6' }}>
                        <Ban size={20} style={{ color: isDark ? '#90A1B9' : '#6B7280' }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium" style={{ color: titleColor }}>Clear Cancelled</h3>
                        <p className="text-xs" style={{ color: subtitleColor }}>{formatNumber(jobStats.byStatus.cancelled)} jobs</p>
                      </div>
                    </div>
                  </button>

                  {/* Clear by User */}
                  <button
                    onClick={() => setShowClearByUserModal(true)}
                    disabled={actionLoading}
                    className="p-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    style={{ border: `1px solid ${isDark ? '#2E1A5E' : '#E9D5FF'}` }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#1E1338' : '#F3E8FF' }}>
                        <User size={20} style={{ color: isDark ? '#C4B5FD' : '#7C3AED' }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium" style={{ color: titleColor }}>Clear by User</h3>
                        <p className="text-xs" style={{ color: subtitleColor }}>Select a user</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Job Types Breakdown */}
              {jobStats.byType && Object.keys(jobStats.byType).length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-4" style={{ color: titleColor }}>Jobs by Type</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {Object.entries(jobStats.byType).map(([type, count]) => (
                      <div key={type} className="rounded-lg p-3" style={{ background: isDark ? '#1D293D' : '#F9FAFB', border: `1px solid ${isDark ? '#314158' : '#E5E7EB'}` }}>
                        <div className="text-xs font-medium capitalize mb-1" style={{ color: subtitleColor }}>{type}</div>
                        <div className="text-lg font-bold" style={{ color: titleColor }}>{formatNumber(count)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Jobs List */}
          {activeTab === 'jobs' && (
            <>
              {/* Filters - collapsible */}
              {showFilters && (
                <div style={{ background: isDark ? '#1D293D' : '#F8FAFC', borderRadius: '12px', padding: '16px', border: `1px solid ${cardBorder}` }}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold" style={{ color: titleColor }}>Filters</h2>
                    <button
                      onClick={resetFilters}
                      className="text-xs font-medium"
                      style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }}
                    >
                      Reset Filters
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Status Filter */}
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>Status</label>
                      <select
                        value={jobsFilters.status || ''}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    {/* Start Date Filter */}
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>Start Date</label>
                      <input
                        type="date"
                        value={jobsFilters.startDate || ''}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                      />
                    </div>

                    {/* End Date Filter */}
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>End Date</label>
                      <input
                        type="date"
                        value={jobsFilters.endDate || ''}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                      />
                    </div>

                    {/* Sort Order */}
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>Sort Order</label>
                      <select
                        value={jobsFilters.sortOrder || 'desc'}
                        onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                        className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                      >
                        <option value="desc">Newest First</option>
                        <option value="asc">Oldest First</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Jobs Table - no separate card wrapper, just border and overflow-hidden */}
              <div className={`overflow-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ border: `1px solid ${tableBorderColor}`, borderRadius: '12px' }}>
                {jobsLoading ? (
                  <div className="p-8 text-center">
                    <Spinner size="medium" text="Loading jobs..." />
                  </div>
                ) : jobs.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: isDark ? '#1D293D' : '#F3F4F6' }}>
                      <Inbox size={28} style={{ color: subtitleColor }} />
                    </div>
                    <h3 className="text-sm font-medium mb-1" style={{ color: titleColor }}>No jobs found</h3>
                    <p className="text-xs" style={{ color: subtitleColor }}>Try adjusting your filters</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                          <tr style={{ background: tableHeaderBg }}>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Job Type
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Account
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              User
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Created
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobs.map((job) => (
                            <tr
                              key={job._id}
                              className="cursor-pointer transition-colors"
                              style={{ borderBottom: `1px solid ${tableBorderColor}` }}
                              onClick={() => openJobDetails(job)}
                              onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? '#1D293D' : '#FAF5FF')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              <td className="px-4 py-3" style={{ borderBottom: `1px solid ${tableBorderColor}` }}>
                                <div className="max-w-[200px]">
                                  <span className="text-sm font-medium capitalize truncate block" style={{ color: titleColor }} title={formatJobType(job.job_type)}>
                                    {formatJobType(job.job_type)}
                                  </span>
                                  {job.error && (
                                    <span className="text-xs truncate mt-0.5 flex items-center gap-1" style={{ color: isDark ? '#F87171' : '#EF4444' }} title={job.error}>
                                      <AlertTriangle size={10} />
                                      {job.error.length > 30 ? job.error.substring(0, 30) + '...' : job.error}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap" style={{ borderBottom: `1px solid ${tableBorderColor}` }}>
                                {getStatusBadge(job.status)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap" style={{ borderBottom: `1px solid ${tableBorderColor}` }}>
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center">
                                    <Instagram size={14} className="text-white" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium" style={{ color: titleColor }}>
                                      {job.account?.username || '-'}
                                    </p>
                                    <p className="text-xs capitalize" style={{ color: subtitleColor }}>
                                      {job.account?.platform || '-'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap" style={{ borderBottom: `1px solid ${tableBorderColor}` }}>
                                {job.user ? (
                                  <div>
                                    <p className="text-sm font-medium" style={{ color: titleColor }}>
                                      {job.user.fullName}
                                    </p>
                                    <p className="text-xs" style={{ color: subtitleColor }}>
                                      {job.user.email}
                                    </p>
                                  </div>
                                ) : (
                                  <span className="text-xs" style={{ color: subtitleColor }}>-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                                {formatDate(job.created_on || job.createdAt)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap" style={{ borderBottom: `1px solid ${tableBorderColor}` }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openJobDetails(job);
                                  }}
                                  className="px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                  style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? '#1C398E' : '#EFF6FF')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                  <Eye size={12} />
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" style={{ background: isDark ? '#1D293D' : '#F9FAFB', borderTop: `1px solid ${tableBorderColor}` }}>
                      <div className="text-sm" style={{ color: paginationText }}>
                        Showing <span className="font-medium">{(jobsPagination.page - 1) * jobsPagination.limit + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(jobsPagination.page * jobsPagination.limit, jobsPagination.total)}
                        </span>{' '}
                        of <span className="font-medium">{formatNumber(jobsPagination.total)}</span> jobs
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(jobsPagination.page - 1)}
                          disabled={jobsPagination.page === 1}
                          className="px-3 py-1.5 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: paginationBg, border: `1px solid ${paginationBorder}`, color: paginationText }}
                        >
                          Previous
                        </button>
                        <span className="text-sm" style={{ color: paginationText }}>
                          Page {jobsPagination.page} of {jobsPagination.totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(jobsPagination.page + 1)}
                          disabled={jobsPagination.page >= jobsPagination.totalPages}
                          className="px-3 py-1.5 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: paginationBg, border: `1px solid ${paginationBorder}`, color: paginationText }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

        {/* Clear All Jobs Modal */}
        {showClearAllModal && ReactDOM.createPortal(
          <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
            <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}>
                  <AlertTriangle size={20} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                </div>
                <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Clear All Jobs</h3>
              </div>
              <p className="text-sm mb-4" style={{ color: subtitleColor }}>
                This will clear all jobs from the database. This action cannot be undone.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1" style={{ color: titleColor }}>
                  Clear jobs before date (optional)
                </label>
                <input
                  type="date"
                  value={beforeDate}
                  onChange={(e) => setBeforeDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                />
                <p className="text-xs mt-1" style={{ color: subtitleColor }}>Leave empty to clear all jobs</p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowClearAllModal(false);
                    setBeforeDate('');
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                  style={{ background: buttonBg, color: buttonText }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = buttonHoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = buttonBg)}
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearAllJobs}
                  disabled={actionLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading && <Loader2 size={14} className="animate-spin" />}
                  Clear All
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Clear by Status Modal */}
        {showClearByStatusModal && ReactDOM.createPortal(
          <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
            <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#2A2008' : '#FFF7ED' }}>
                  <Filter size={20} style={{ color: isDark ? '#FACC15' : '#EA580C' }} />
                </div>
                <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Clear Jobs by Status</h3>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1" style={{ color: titleColor }}>Select Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                >
                  <option value="">Select a status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowClearByStatusModal(false);
                    setSelectedStatus('');
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                  style={{ background: buttonBg, color: buttonText }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = buttonHoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = buttonBg)}
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearByStatus}
                  disabled={actionLoading || !selectedStatus}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading && <Loader2 size={14} className="animate-spin" />}
                  Clear Jobs
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Clear by User Modal */}
        {showClearByUserModal && ReactDOM.createPortal(
          <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
            <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#1E1338' : '#F3E8FF' }}>
                  <User size={20} style={{ color: isDark ? '#C4B5FD' : '#7C3AED' }} />
                </div>
                <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Clear Jobs by User</h3>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1" style={{ color: titleColor }}>Search User</label>
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 mb-2"
                  style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: inputText }}
                />
                <div className={`max-h-48 overflow-y-auto rounded-md ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ border: `1px solid ${cardBorder}` }}>
                  {usersLoading ? (
                    <div className="p-4 text-center text-sm" style={{ color: subtitleColor }}>Loading...</div>
                  ) : users.length === 0 ? (
                    <div className="p-4 text-center text-sm" style={{ color: subtitleColor }}>No users found</div>
                  ) : (
                    users.map((user) => (
                      <div
                        key={user._id}
                        onClick={() => setSelectedUserId(user._id)}
                        className="p-3 cursor-pointer transition-colors"
                        style={{
                          background: selectedUserId === user._id ? (isDark ? '#1E1338' : '#FAF5FF') : 'transparent',
                          borderBottom: `1px solid ${isDark ? '#1E293B' : '#F3F4F6'}`
                        }}
                        onMouseEnter={(e) => { if (selectedUserId !== user._id) e.currentTarget.style.background = isDark ? '#1D293D' : '#F9FAFB'; }}
                        onMouseLeave={(e) => { if (selectedUserId !== user._id) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            checked={selectedUserId === user._id}
                            onChange={() => setSelectedUserId(user._id)}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <div>
                            <p className="text-sm font-medium" style={{ color: titleColor }}>{user.fullName}</p>
                            <p className="text-xs" style={{ color: subtitleColor }}>{user.email}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowClearByUserModal(false);
                    setSelectedUserId('');
                    setUserSearchTerm('');
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                  style={{ background: buttonBg, color: buttonText }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = buttonHoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = buttonBg)}
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearByUser}
                  disabled={actionLoading || !selectedUserId}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-500 rounded-md hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading && <Loader2 size={14} className="animate-spin" />}
                  Clear Jobs
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Job Details Modal */}
        {showJobDetailsModal && selectedJob && ReactDOM.createPortal(
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
            <div className="rounded-lg max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col" style={{ background: modalBg }}>
              {/* Modal Header */}
              <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${cardBorder}`, background: isDark ? 'linear-gradient(to right, #1E1338, #1D293D)' : 'linear-gradient(to right, #FAF5FF, #FFFFFF)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#1E1338' : '#F3E8FF' }}>
                    <FileText size={20} style={{ color: isDark ? '#C4B5FD' : '#7C3AED' }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Job Details</h3>
                    <p className="text-xs" style={{ color: subtitleColor }}>ID: {selectedJob._id}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowJobDetailsModal(false);
                    setSelectedJob(null);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full transition-colors"
                  onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? '#253449' : '#F3F4F6')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <X size={18} style={{ color: subtitleColor }} />
                </button>
              </div>

              {/* Modal Body - Scrollable */}
              <div className={`flex-1 min-h-0 overflow-y-auto p-6 space-y-4 ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}>
                {/* Compact Info Card */}
                <div className="rounded-lg" style={{ background: modalInfoBg, border: `1px solid ${cardBorder}` }}>
                  {/* Row 1: Status, Timestamps, Account */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 p-4">
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>Status</p>
                      {getStatusBadge(selectedJob.status)}
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>Created</p>
                      <p className="text-xs font-medium" style={{ color: titleColor }}>{formatDate(selectedJob.created_on || selectedJob.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>Scheduled</p>
                      <p className="text-xs font-medium" style={{ color: titleColor }}>{formatDate(selectedJob.scheduled_for)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>Actioned Before</p>
                      <p className="text-xs font-medium" style={{ color: titleColor }}>{formatDate(selectedJob.actioned_before)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>Completed</p>
                      <p className="text-xs font-medium" style={{ color: titleColor }}>{formatDate(selectedJob.completed_at)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>Account</p>
                      {selectedJob.account ? (
                        <p className="text-xs font-medium" style={{ color: titleColor }}>@{selectedJob.account.username}</p>
                      ) : (
                        <p className="text-xs" style={{ color: subtitleColor }}>-</p>
                      )}
                    </div>
                  </div>
                  {/* Row 2: User, Proxy, Instance */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-4 pb-4 pt-3" style={{ borderTop: `1px solid ${cardBorder}` }}>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>User</p>
                      {selectedJob.user ? (
                        <div>
                          <p className="text-xs font-medium" style={{ color: titleColor }}>{selectedJob.user.fullName || '-'}</p>
                          <p className="text-[10px]" style={{ color: subtitleColor }}>{selectedJob.user.email || ''}</p>
                        </div>
                      ) : (
                        <p className="text-xs" style={{ color: subtitleColor }}>-</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>Proxy</p>
                      {selectedJob.proxy && selectedJob.proxy._id ? (
                        <div>
                          <p className="text-xs font-medium break-all" style={{ color: titleColor }}>{selectedJob.proxy.proxy || '-'}</p>
                          <p className="text-[10px] capitalize" style={{ color: subtitleColor }}>{selectedJob.proxy.provider || ''}{selectedJob.proxy.country ? ` · ${selectedJob.proxy.country}` : ''}</p>
                        </div>
                      ) : (
                        <p className="text-xs" style={{ color: subtitleColor }}>-</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: subtitleColor }}>Anbox Instance</p>
                      {selectedJob.instance && selectedJob.instance._id ? (
                        <div>
                          <p className="text-xs font-medium" style={{ color: titleColor }}>{selectedJob.instance.instance_name || '-'}</p>
                          <p className="text-[10px]" style={{ color: subtitleColor }}>
                            {selectedJob.instance.status || ''}
                            {selectedJob.instance.machine_id ? ` · ${selectedJob.instance.machine_id.slice(0, 12)}...` : ''}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs" style={{ color: subtitleColor }}>-</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Execution Logs */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold" style={{ color: titleColor }}>
                      Execution Logs
                      {executionLogsPagination.total > 0 && (
                        <span className="ml-2 text-xs font-normal" style={{ color: subtitleColor }}>({executionLogsPagination.total})</span>
                      )}
                    </h4>
                    <button
                      onClick={() => setLogsAutoRefresh(prev => !prev)}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors"
                      style={{
                        background: logsAutoRefresh ? (isDark ? '#0C1A2E' : '#EFF6FF') : (isDark ? '#1D293D' : '#F3F4F6'),
                        color: logsAutoRefresh ? (isDark ? '#60A5FA' : '#2563EB') : subtitleColor,
                        border: `1px solid ${logsAutoRefresh ? (isDark ? '#1E3A5F' : '#BFDBFE') : cardBorder}`
                      }}
                    >
                      <RefreshCcw size={12} className={logsAutoRefresh ? 'animate-spin' : ''} style={{ animationDuration: '3s' }} />
                      Auto-refresh {logsAutoRefresh ? 'ON' : 'OFF'}
                    </button>
                  </div>

                  {executionLogsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={24} className="animate-spin" style={{ color: subtitleColor }} />
                    </div>
                  ) : executionLogs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 rounded-lg" style={{ background: modalInfoBg, border: `1px solid ${cardBorder}` }}>
                      <Inbox size={32} style={{ color: subtitleColor }} />
                      <p className="mt-2 text-sm" style={{ color: subtitleColor }}>No execution logs found</p>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${cardBorder}` }}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr style={{ background: tableHeaderBg }}>
                              <th className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider" style={{ color: subtitleColor, width: '70px' }}>Level</th>
                              <th className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>Message</th>
                              <th className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider" style={{ color: subtitleColor, width: '200px' }}>Error</th>
                              <th className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider" style={{ color: subtitleColor, width: '150px' }}>Timestamp</th>
                            </tr>
                          </thead>
                          <tbody>
                            {executionLogs.map((log) => (
                              <tr key={log._id} style={{ borderTop: `1px solid ${tableBorderColor}` }}>
                                <td className="px-3 py-2">{getLogLevelBadge(log.level)}</td>
                                <td className="px-3 py-2 text-xs" style={{ color: titleColor }}>{log.message || '-'}</td>
                                <td className="px-3 py-2 text-xs" style={{ color: log.error_message ? (isDark ? '#F87171' : '#DC2626') : subtitleColor }}>
                                  {log.error_message || '-'}
                                </td>
                                <td className="px-3 py-2 text-xs whitespace-nowrap" style={{ color: subtitleColor }}>
                                  {formatDate(log.timestamp || log.created_at)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Logs Pagination */}
                      {executionLogsPagination.totalPages > 1 && (
                        <div className="flex items-center justify-between mt-3 px-1">
                          <p className="text-xs" style={{ color: subtitleColor }}>
                            Page {executionLogsPagination.page} of {executionLogsPagination.totalPages}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleLogsPageChange(executionLogsPagination.page - 1)}
                              disabled={executionLogsPagination.page <= 1}
                              className="px-3 py-1 text-xs font-medium rounded transition-colors disabled:opacity-40"
                              style={{ background: paginationBg, border: `1px solid ${paginationBorder}`, color: paginationText }}
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => handleLogsPageChange(executionLogsPagination.page + 1)}
                              disabled={executionLogsPagination.page >= executionLogsPagination.totalPages}
                              className="px-3 py-1 text-xs font-medium rounded transition-colors disabled:opacity-40"
                              style={{ background: paginationBg, border: `1px solid ${paginationBorder}`, color: paginationText }}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-3 flex justify-end" style={{ borderTop: `1px solid ${cardBorder}`, background: isDark ? '#111827' : '#F9FAFB' }}>
                <button
                  onClick={() => {
                    setShowJobDetailsModal(false);
                    setSelectedJob(null);
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                  style={{ background: mainBg, border: `1px solid ${cardBorder}`, color: buttonText }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = buttonHoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = mainBg)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
